/*
 * Shared tournament UI helpers — pills, label maps, date formatting.
 * Mirrors apps/mobile/src/screens/tournaments/shared.tsx where it applies.
 */

import type { ReactNode } from 'react';
import type {
  EventStatus, EventVisibility, WinningRuleType,
  FormatType, RoundStatus, MatchStatus, ScoringMode,
} from '@golf-app/shared';

export function StatusPill({ status }: { status: EventStatus }) {
  const cls =
    status === 'draft' ? 't-pill-draft' :
    status === 'active' ? 't-pill-active' :
    status === 'completed' ? 't-pill-completed' :
    't-pill-archived';
  const label = status[0].toUpperCase() + status.slice(1);
  return <span className={`t-pill ${cls}`}>{label}</span>;
}

export const WINNING_RULES: { value: WinningRuleType; label: string; hint: string }[] = [
  { value: 'most_points_wins', label: 'Most points wins', hint: 'Outright winner — first team past half the points clinches.' },
  { value: 'tie_allowed', label: 'Tie allowed', hint: 'A draw is a valid outcome. Reaching half guarantees at least a tie.' },
  { value: 'defending_champion_retains', label: 'Defender retains on a tie', hint: 'On a draw, the defending team keeps the cup.' },
  { value: 'playoff_required', label: 'Playoff required on a tie', hint: 'Ties go to a sudden-death playoff.' },
];

export const VISIBILITY_OPTIONS: { value: EventVisibility; label: string; hint: string }[] = [
  { value: 'private',     label: 'Private',     hint: 'Only people you invite can see the event.' },
  { value: 'invite_only', label: 'Invite only',  hint: 'Anyone with a direct invite link can view.' },
  { value: 'public_view', label: 'Public view',  hint: 'Read-only public page — great for a leaderboard share.' },
];

export function winningRuleLabel(rule: WinningRuleType): string {
  return WINNING_RULES.find((r) => r.value === rule)?.label ?? rule;
}

export function visibilityLabel(v: EventVisibility): string {
  return VISIBILITY_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

/* ── Shared (i) hint bodies ─────────────────────────────────────────
   One source of truth for the handicap-method and baseline explainers
   used across CreateRound, EditRound, EditTournament, and RoundDetail.
   Format per Travis's spec: no generic intro sentence, just the two
   options as clearly-separated labeled paragraphs. */

function HintBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, color: 'var(--green-bright)', textTransform: 'uppercase', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ color: 'var(--text)', fontSize: 14, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

export function MethodHintBody() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <HintBlock label="Net Course Rating">
        Adjusts for tee difficulty using slope and rating, so harder tees give
        more strokes. A 12.5 index on slope 132 / rating 73.2 / par 72 plays
        as a 16.
      </HintBlock>
      <HintBlock label="Net Straight Handicap">
        The index plays as-is, no tee adjustment. A 12.5 rounds to 13 — one
        stroke on each of the 13 hardest holes.
      </HintBlock>
    </div>
  );
}

export function BaselineHintBody() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <HintBlock label="Low man zero">
        The lowest handicap in the match plays as scratch and everyone else
        gets the difference. A 4 vs a 14: the 4 gets no strokes, the 14 gets
        10.
      </HintBlock>
      <HintBlock label="Full difference">
        Everyone plays their full handicap — no netting against the low man.
        The 4 gets 4 strokes, the 14 gets all 14.
      </HintBlock>
    </div>
  );
}

/** Format an ISO date (YYYY-MM-DD) without timezone surprises. */
export function formatEventDate(iso: string | null): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const date = new Date(+y, +mo - 1, +d);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateRange(start: string | null, end: string | null): string {
  const s = formatEventDate(start);
  const e = formatEventDate(end);
  if (s && e) return `${s} — ${e}`;
  return s ?? e ?? '';
}

// ── Format / round / match helpers (W2+) ────────────────────────────────────
export const FORMAT_LABELS: Record<FormatType, string> = {
  singles_match_play: 'Singles',
  four_ball_better_ball: 'Four-Ball (Better Ball)',
  foursomes_alternate_shot: 'Foursomes (Alt Shot)',
  scramble_match_play: 'Scramble',
  shamble_match_play: 'Shamble',
  aggregate_match_play: 'Aggregate',
  manual_result_only: 'Manual Result',
};

export const FORMAT_OPTIONS: { value: FormatType; label: string; hint: string }[] = [
  { value: 'singles_match_play', label: 'Singles', hint: '1 v 1 match play' },
  { value: 'four_ball_better_ball', label: 'Four-Ball', hint: '2 v 2, best ball per side' },
  { value: 'foursomes_alternate_shot', label: 'Foursomes', hint: '2 v 2, alternate shot' },
  { value: 'scramble_match_play', label: 'Scramble', hint: '2–4 per side, team scramble' },
  { value: 'shamble_match_play', label: 'Shamble', hint: '2–4 per side, shamble' },
  { value: 'aggregate_match_play', label: 'Aggregate', hint: 'Sum of side scores' },
  { value: 'manual_result_only', label: 'Manual Result', hint: 'Enter the final result only' },
];

export const SCORING_MODE_LABELS: Record<ScoringMode, string> = {
  gross: 'Gross', net: 'Net', gross_and_net: 'Gross & Net', stableford: 'Stableford',
};

export function formatLabel(f: FormatType): string {
  return FORMAT_LABELS[f] ?? f;
}

const ROUND_STATUS_META: Record<RoundStatus, { label: string; cls: string }> = {
  draft:       { label: 'Draft',     cls: 't-pill-draft' },
  published:   { label: 'Published', cls: 't-pill-active' },
  locked:      { label: 'Locked',    cls: 't-pill-locked' },
  in_progress: { label: 'Live',      cls: 't-pill-active' },
  completed:   { label: 'Completed', cls: 't-pill-completed' },
};

export function RoundStatusPill({ status }: { status: RoundStatus }) {
  const m = ROUND_STATUS_META[status];
  return <span className={`t-pill ${m.cls}`}>{m.label}</span>;
}

const MATCH_STATUS_META: Record<MatchStatus, { label: string; cls: string }> = {
  draft:                { label: 'Draft',     cls: 't-pill-draft' },
  published:            { label: 'Set',       cls: 't-pill-active' },
  ready:                { label: 'Ready',     cls: 't-pill-active' },
  in_progress:          { label: 'Live',      cls: 't-pill-active' },
  pending_confirmation: { label: 'Pending',   cls: 't-pill-locked' },
  disputed:             { label: 'Disputed',  cls: 't-pill-disputed' },
  final:                { label: 'Final',     cls: 't-pill-completed' },
  cancelled:            { label: 'Cancelled', cls: 't-pill-archived' },
};

export function MatchStatusPill({ status }: { status: MatchStatus }) {
  // Draft is the default state and not interesting to surface — the match
  // card itself reads as "being built" when it has a Remove button. Skip
  // rendering anything for draft so the card header stays clean.
  if (status === 'draft') return null;
  const m = MATCH_STATUS_META[status];
  return <span className={`t-pill ${m.cls}`}>{m.label}</span>;
}
